import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion, useScroll, useTransform } from "motion/react";
import { ArrowRight } from "@phosphor-icons/react";
import { EASE_OUT, MaskWords, PageFrame, Seo } from "../../kit.jsx";
import { MAIL, Rise, mailto } from "./shared.jsx";
import "./join.css";

const mail = <a href={mailto()}>{MAIL}</a>;

const PRIVACY = [
  {
    id: "information-you-send",
    title: "Information you choose to send",
    body: [<>The interest form prepares an email on your device. The website does not claim that a submission has been stored or sent automatically.</>],
  },
  {
    id: "email-enquiries",
    title: "Email enquiries",
    body: [<>Contact links open your email application. If you send an email, its contents and your email address are shared with the recipient. Only include information needed for your enquiry.</>],
  },
  {
    id: "public-programme-information",
    title: "Public programme information",
    body: [<>Opportunity and programme details should always be checked against the linked official organiser before acting.</>],
  },
  {
    id: "photographs",
    title: "Photographs",
    body: [<>For a question or removal request about a published photograph, email {mail} with the page address and image description.</>],
  },
  {
    id: "questions",
    title: "Questions",
    body: [<>Email {mail} with privacy questions.</>],
  },
];

const TERMS = [
  {
    id: "informational-use",
    title: "Informational use",
    body: [<>Project GRID resources are educational and informational. They are not a guarantee of selection, funding, mentorship or programme admission.</>],
  },
  {
    id: "accuracy",
    title: "Accuracy",
    body: [<>Time-sensitive opportunities can change. Check the official source before registering or sharing personal information.</>],
  },
  {
    id: "programme-details",
    title: "Programme details",
    body: [<>A past programme account is a record of an event. Participation in a future programme depends on the terms and eligibility published for that programme.</>],
  },
  {
    id: "respect-and-credit",
    title: "Respect and credit",
    body: [<>Do not copy student work without permission. Credit creators, sources and programme partners accurately.</>],
  },
  {
    id: "photographs-and-materials",
    title: "Using photographs and materials",
    body: [<>Contact GRID before reusing programme photographs. Other organisations' names and marks remain associated with their respective owners. Write to {mail}.</>],
  },
];

function useActiveSection(ids) {
  const [active, setActive] = useState(ids[0]);
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((entry) => entry.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActive(visible[0].target.id);
      },
      { rootMargin: "-20% 0px -55% 0px" },
    );
    ids.forEach((id) => {
      const element = document.getElementById(id);
      if (element) observer.observe(element);
    });
    return () => observer.disconnect();
  }, [ids]);
  return active;
}

export default function LegalPage({ kind }) {
  const privacy = kind === "privacy";
  const sections = privacy ? PRIVACY : TERMS;
  const [ids] = useState(() => sections.map((section) => section.id));
  const active = useActiveSection(ids);
  const reduce = useReducedMotion();
  const articleRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: articleRef, offset: ["start 0.3", "end 0.7"] });
  const progress = useTransform(scrollYProgress, (v) => `scaleY(${Math.min(1, Math.max(0, v))})`);

  return (
    <PageFrame className="rd-page jn-page jn-lg">
      <Seo
        title={privacy ? "Privacy" : "Terms"}
        path={`/${kind}`}
        description={privacy ? "How Project GRID handles information submitted through this website." : "Basic terms for using the Project GRID website."}
      />
      <main>
        <header className="jn-wrap jn-lg-hero">
          <Rise as="p" className="jn-mono" delay={0.05}>{privacy ? "Privacy" : "Terms"} · projectgrid.org</Rise>
          <MaskWords as="h1" className="jn-h1 jn-lg-hero__title" text={privacy ? "Collect less. Explain clearly." : "Use the site thoughtfully."} onMount delay={0.1} />
          <Rise as="p" className="jn-lede" delay={0.4}>
            {privacy
              ? "This website is designed to minimise personal information collection until formal submission systems are in place."
              : "These launch terms explain the intended use of Project GRID's public information and resources."}
          </Rise>
        </header>
        <div className="jn-wrap jn-lg-body">
          <nav className="jn-lg-toc" aria-label="On this page">
            <p className="jn-mono">On this page</p>
            <div className="jn-lg-toc__inner">
              <span className="jn-lg-toc__track" aria-hidden="true">
                <motion.span style={reduce ? { transform: "scaleY(1)" } : { transform: progress }} />
              </span>
              <ol>
                {sections.map((section, index) => (
                  <li key={section.id}>
                    <a href={`#${section.id}`} aria-current={active === section.id ? "location" : undefined} className={active === section.id ? "is-on" : ""}>
                      {active === section.id && (
                        <motion.span className="jn-lg-toc__fill" layoutId="jn-lg-fill" transition={{ duration: reduce ? 0 : 0.4, ease: EASE_OUT }} />
                      )}
                      <span className="jn-lg-toc__num">{index + 1}</span>
                      <span className="jn-lg-toc__label">{section.title}</span>
                    </a>
                  </li>
                ))}
              </ol>
            </div>
          </nav>
          <article className="jn-lg-article" ref={articleRef}>
            {sections.map((section, index) => (
              <section key={section.id} id={section.id} className="jn-lg-section" aria-labelledby={`${section.id}-title`}>
                <span className="jn-lg-section__num" aria-hidden="true">{String(index + 1).padStart(2, "0")}</span>
                <h2 id={`${section.id}-title`}>{section.title}</h2>
                {section.body.map((paragraph, pIndex) => <p key={pIndex}>{paragraph}</p>)}
              </section>
            ))}
            <footer className="jn-lg-foot">
              <p>Questions about this page go to {mail}.</p>
              <Link className="gh-link" to={privacy ? "/terms" : "/privacy"}>
                Read the {privacy ? "terms" : "privacy notes"} <ArrowRight weight="bold" aria-hidden="true" />
              </Link>
            </footer>
          </article>
        </div>
      </main>
    </PageFrame>
  );
}
